import { useEffect, useRef } from 'react';
import { Node } from 'reactflow';
import { canvasState } from '@components/MainStage/store';

export const LABEL_SYNC_DELAY = 300;

type NodeLabelSyncOptions = {
  activeNode: Node | undefined;
  name: string | undefined;
};

/**
 * Mirrors the name being edited in a drawer onto its canvas node, debounced,
 * and restores the node's persisted name when the drawer goes away.
 *
 * The restore reads the store at unmount rather than a value captured during
 * render: saving renames the node and closes the drawer in one batch, so no
 * render happens in between and a captured name would be the pre-save one.
 * A node still being added has no persisted name, so cancelling blanks its
 * label rather than leaving the abandoned text on the canvas. A node that has
 * left the store is not written to at all.
 */
const useNodeLabelSync = ({ activeNode, name }: NodeLabelSyncOptions) => {
  const latestId = useRef('');

  useEffect(() => {
    latestId.current = activeNode?.id ?? '';
  });

  useEffect(() => {
    const { id, data } = activeNode ?? {};

    if (!id) return undefined;

    const timeout = setTimeout(() => {
      canvasState().updateNodeLabel(id, name || data?.dto?.name || '');
    }, LABEL_SYNC_DELAY);

    return () => clearTimeout(timeout);
  }, [name, activeNode?.id]);

  useEffect(
    () => () => {
      const id = latestId.current;

      if (!id) return;

      const { nodes, updateNodeLabel } = canvasState();
      const persisted = nodes.find((node) => node.id === id);

      if (persisted) updateNodeLabel(id, persisted.data?.dto?.name ?? '');
    },
    [],
  );
};

export default useNodeLabelSync;
